import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import DataTable from '../components/DataTable';
import { fmt_dt, fmt_usd } from './helpers';
import type { DatasetSummary, DatasetsResponse } from './types';

// Business 360 index — the 50 most recent Discovery datasets. Row click drills into the
// dataset's painters (spec §5). The proxy maps any upstream error to an empty list, so this
// view never error-pages; it shows the empty state instead.
export default function DatasetsList() {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['b360_datasets'],
    queryFn: () => api.get<DatasetsResponse>('/api/business360/datasets'),
  });

  const columns = [
    {
      header: 'Vertical',
      cell: (d: DatasetSummary) => <strong style={{ textTransform: 'capitalize' }}>{d.vertical}</strong>,
    },
    { header: 'Created', cell: (d: DatasetSummary) => fmt_dt(d.created_at), width: '190px' },
    {
      header: 'Records',
      cell: (d: DatasetSummary) => d.record_count,
      width: '100px',
    },
    {
      header: 'Build-ready',
      cell: (d: DatasetSummary) => (
        <span>
          <strong>{d.build_ready_count}</strong>
          <span className="muted"> / {d.record_count}</span>
        </span>
      ),
      width: '120px',
    },
    { header: 'Variable cost', cell: (d: DatasetSummary) => fmt_usd(d.variable_cost_usd), width: '120px' },
    {
      header: 'Dataset',
      cell: (d: DatasetSummary) => <code style={{ fontSize: 12 }}>{d.dataset_id}</code>,
    },
  ];

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {isLoading && <p style={{ padding: 24, margin: 0 }}>Loading datasets…</p>}
      {error && (
        <p className="error" style={{ margin: 24 }}>
          Failed to load: {error instanceof ApiError ? error.reason : String(error)}
        </p>
      )}
      {data && (
        <DataTable
          rows={data.datasets}
          columns={columns}
          on_row_click={(d) => navigate(`/business360/${encodeURIComponent(d.dataset_id)}`)}
          empty_message="No Business 360 datasets yet."
        />
      )}
    </div>
  );
}
